import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import {
  Box,
  Heading,
  Image,
  Link,
  Text,
  useToast,
} from '@redriverpress/system'

import CheckboxField from 'Common/components/forms/CheckboxField'
import Backpack from 'Images/summary_backpack.svg'
import Loc from '../common/components/Loc'
import { useStudentOnboardingContext } from '../context/onboardingStudentContext'
import { STD_ONBOARDING_PATH } from '../common/constants'
import { submitSignup } from '../common/services'
import BottomNav from '../common/components/BottomNav'

export default function Summary() {
  const history = useHistory()
  const { addToast } = useToast()
  const [
    { classCode, email, firstName, lastName, locale, password, yumiClass },
  ] = useStudentOnboardingContext()

  const [loading, setLoading] = useState(false)
  const [signupError, setSignupError] = useState(null)

  const handleRegistration = () => {
    setLoading(true)
    setSignupError(null)

    const data = {
      user: {
        email,
        first_name: firstName,
        last_name: lastName,
        password,
        yumi_class_code: classCode,
      },
    }

    submitSignup(data)
      .then(() => {
        window.location = '/students/dashboard?first_time=true'
      })
      .catch(err => {
        setLoading(false)
        setSignupError(err)
        // eslint-disable-next-line no-console
        console.warn(signupError)
        addToast({
          id: 'registration-error',
          content: 'There was a problem. Please try again.',
          type: 'warning',
        })
      })
  }

  const schema = Yup.object().shape({
    acceptedTerms: Yup.boolean()
      .required()
      .oneOf([true], 'You must accept the terms'),
  })

  if (!password) return null

  return (
    <>
      <Box display='flex' flexDirection='column' minHeight='90vh'>
        <Box display='flex' flex='1'>
          <Box
            flex={1}
            width={[null, null, '20%', '50%']}
            display='flex'
            justifyContent='center'
          >
            <Image
              display={[null, null, 'none', 'block']}
              width='200px'
              ml='10%'
              src={Backpack}
              alt='Backpack'
            />
          </Box>
          <Box
            display='flex'
            flexDirection='column'
            width={[1, null, '80%', '50%']}
            ml={[0, null, 'auto']}
            pl={[0, null, 4]}
            maxHeight='90vh'
            overflow='auto'
          >
            <Box mb={3} display='flex' flexDirection='column'>
              <Heading fontSize={[3, null, 4]} mb={0}>
                Summary
              </Heading>
              {!!locale && (
                <Loc
                  fontSize={[1, null, 2]}
                  color='gray700'
                  fontWeight='600'
                  copy='Summary'
                />
              )}
            </Box>

            <Box
              display='flex'
              flexDirection='column'
              justifyContent='space-evenly'
              height='53vh'
            >
              {!yumiClass.invitationDefaultClass && (
                <Box>
                  <Text
                    fontSize={[1, null, 2]}
                    fontWeight='600'
                    mt={2}
                    color='gray600'
                  >
                    Class name
                  </Text>
                  <Text
                    fontSize={[2, null, 3]}
                    color='gray900'
                    fontWeight='600'
                    mt={0}
                    mb={2}
                  >
                    {yumiClass.name}
                  </Text>
                </Box>
              )}

              <Box>
                <Text
                  fontSize={[1, null, 2]}
                  fontWeight='600'
                  mt={2}
                  color='gray600'
                >
                  First name
                </Text>
                <Text
                  fontSize={[2, null, 3]}
                  color='gray900'
                  fontWeight='600'
                  mt={0}
                  mb={2}
                >
                  {firstName}
                </Text>
              </Box>

              <Box>
                <Text
                  fontSize={[1, null, 2]}
                  fontWeight='600'
                  mt={2}
                  color='gray600'
                >
                  Last name
                </Text>
                <Text
                  fontSize={[2, null, 3]}
                  color='gray900'
                  fontWeight='600'
                  mt={0}
                  mb={2}
                >
                  {lastName}
                </Text>
              </Box>

              <Box>
                <Text
                  fontSize={[1, null, 2]}
                  fontWeight='600'
                  mt={2}
                  color='gray600'
                >
                  Email address
                </Text>
                <Text
                  fontSize={[2, null, 3]}
                  color='gray900'
                  fontWeight='600'
                  mt={0}
                  mb={2}
                >
                  {email}
                </Text>
              </Box>

              <Box>
                <Text
                  fontSize={[1, null, 2]}
                  fontWeight='600'
                  mt={2}
                  color='gray600'
                >
                  Password
                </Text>
                <Text
                  fontSize={[2, null, 3]}
                  color='gray900'
                  fontWeight='600'
                  mt={0}
                  mb={2}
                >
                  ********
                </Text>
              </Box>
            </Box>

            <Formik
              initialValues={{ acceptedTerms: false }}
              initialErrors={{ acceptedTerms: false }}
              validationSchema={schema}
              validateOnBlur
              validateOnChange
              style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
            >
              {formik => {
                const { isValid } = formik

                return (
                  <Form
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                    }}
                  >
                    <Box
                      mt={3}
                      bg='blue100'
                      borderRadius='rounded'
                      display='flex'
                      p={[2, null, 3]}
                    >
                      <Box as='label'>
                        <CheckboxField name='acceptedTerms' />
                      </Box>
                      <Box as='label' htmlFor='acceptedTerms' ml={3} flex='1'>
                        <Text fontSize={1}>
                          I agree to Ellii’s terms and privacy policy.{' '}
                          <Link
                            color='black'
                            href='/students/terms'
                            target='_blank'
                          >
                            {' '}
                            View the Privacy Policy and Terms
                          </Link>
                          .
                        </Text>
                      </Box>
                    </Box>
                    {!!locale && (
                      <Loc
                        fontSize={0}
                        color='gray700'
                        fontWeight='400'
                        copy='I agree to Ellii’s terms'
                        mb={3}
                      />
                    )}

                    {/* Footer w/ nav */}
                    <BottomNav
                      prevLink={() =>
                        history.push({
                          pathname: `${STD_ONBOARDING_PATH.signup}/create-password`,
                        })
                      }
                      nextLink={handleRegistration}
                      nextTitle={
                        yumiClass.invitationDefaultClass ? 'Join' : 'Join class'
                      }
                      loading={loading}
                      disabled={!isValid}
                    />
                  </Form>
                )
              }}
            </Formik>
          </Box>
        </Box>
      </Box>
      <Box
        width={[1, null, '80%', '50%']}
        height='100vh'
        bg={['blue50', null, 'blue50']}
        position='absolute'
        top='0'
        right='0'
        zIndex={-1}
      />
    </>
  )
}
